// src/App.tsx
// This is a test comment to force a recompile.
import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { auth } from "./firebase";

import Header from "./components/Header";
import Home from "./pages/Home";
import Societes from "./pages/Societes";
import SocietesFoodinnov from "./pages/SocietesFoodinnov";
import Opportunites from "./pages/Opportunites";
import OpportunitesFoodinnov from "./pages/OpportunitesFoodinnov";
import Devis from "./pages/Devis";
import DevisFoodinnov from "./pages/DevisFoodinnov";
import Factures from "./pages/Factures";
import FacturesFoodinnov from "./pages/FacturesFoodinnov";
import FactureZak from "./pages/FactureZak";
import MyAccount from "./pages/MyAccount";
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import Unauthorized from "./pages/Unauthorized";
import AdminLogs from "./pages/AdminLogs";
import Register from "./pages/Register";
import PdfToolPage from "./pages/PdfToolPage";
import PdfToolPageDevis from "./pages/PdfToolPageDevis";
import FdaToolPage from "./pages/FdaPdfToolPage";
import PdfRepToolPage from "./pages/PdfRepToolPage";
import FactureTool from "./pages/FactureTool";
import FactureZakTool from "./pages/FactureZakTool";
import Database from "./pages/Database";
import MailsTypes from "./pages/MailsTypes";
import TachesAdministration from "./pages/TachesAdministration";
import DataRecovery from "./pages/DataRecovery";

import RoleRoute from "./routes/RoleRoute";
import { DemoProvider } from "./context/DemoContext";
import DemoBanner from "./components/DemoBanner";

interface AuthContextType {
  user: User | null;
  handleLogout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  handleLogout: async () => {},
});

const AppLayout: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        console.log("✅ Utilisateur connecté :", u.email);
      } else {
        console.log("🚪 Utilisateur déconnecté");
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      localStorage.removeItem("isAuthenticated");
      navigate("/login");
    } catch (err) {
      console.error("❌ Erreur lors de la déconnexion :", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, handleLogout }}>
      <Header
        isAuthenticated={Boolean(user)}
        onLoginClick={() => navigate("/login")}
        onHomeClick={() => navigate("/")}
        onLogoutClick={handleLogout}
      />
      <DemoBanner />

      <Routes>
        <Route path="/" element={<Home />} />

        {/* Routes réservées à admin uniquement */}
        <Route
          path="/admin"
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <Admin />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/database"
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <Database />
            </RoleRoute>
          }
        />
        <Route
          path="/societes"
          element={
            <RoleRoute allowedRoles={["admin", "demo"]}>
              <Societes />
            </RoleRoute>
          }
        />
        <Route
          path="/opportunites"
          element={
            <RoleRoute allowedRoles={["admin", "demo"]}>
              <Opportunites />
            </RoleRoute>
          }
        />
        <Route
          path="/devis"
          element={
            <RoleRoute allowedRoles={["admin", "demo"]}>
              <Devis />
            </RoleRoute>
          }
        />
        <Route
          path="/factures"
          element={
            <RoleRoute allowedRoles={["admin", "demo"]}>
              <Factures />
            </RoleRoute>
          }
        />
        <Route
          path="/factures-zak"
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <FactureZak />
            </RoleRoute>
          }
        />
        <Route
          path="/factures-foodinnov"
          element={
            <RoleRoute allowedRoles={["admin", "foodinnov"]}>
              <FacturesFoodinnov />
            </RoleRoute>
          }
        />

        {/* Routes partagées : admin + foodinnov */}
        <Route
          path="/societes-foodinnov"
          element={
            <RoleRoute allowedRoles={["admin", "foodinnov"]}>
              <SocietesFoodinnov />
            </RoleRoute>
          }
        />
        <Route
          path="/opportunites-foodinnov"
          element={
            <RoleRoute allowedRoles={["admin", "foodinnov"]}>
              <OpportunitesFoodinnov />
            </RoleRoute>
          }
        />
        <Route
          path="/devis-foodinnov"
          element={
            <RoleRoute allowedRoles={["admin", "foodinnov"]}>
              <DevisFoodinnov />
            </RoleRoute>
          }
        />

        {/* Accès libre */}
        <Route path="/profil" element={<MyAccount />} />
        <Route path="/login" element={<Login />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/logs" element={<AdminLogs />} />
        <Route path="/register" element={<Register />} />
        
        {/* Outils PDF */}
        <Route path="/admin/pdf-tool" element={<PdfToolPage />} />
        <Route 
          path="/admin/pdf-tool-devis" 
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <PdfToolPageDevis />
            </RoleRoute>
          } 
        />
        <Route 
          path="/admin/fda-tool" 
          element={
            <RoleRoute allowedRoles={["admin", "foodinnov"]}>
              <FdaToolPage />
            </RoleRoute>
          } 
        />
        <Route 
          path="/outil-fda-pdf" 
          element={
            <RoleRoute allowedRoles={["admin", "foodinnov"]}>
              <FdaToolPage />
            </RoleRoute>
          } 
        />
        <Route 
          path="/admin/pdf-rep-tool" 
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <PdfRepToolPage />
            </RoleRoute>
          } 
        />

        {/* Page Bilan */}

        <Route path="/outil-facture-pdf" element={<FactureTool />} />
        <Route path="/outil-facture-zak-pdf" element={<FactureZakTool />} />

        <Route
          path="/mails-types"
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <MailsTypes />
            </RoleRoute>
          }
        />

        <Route
          path="/database"
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <Database />
            </RoleRoute>
          }
        />
        <Route
          path="/taches-administration"
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <TachesAdministration />
            </RoleRoute>
          }
        />
        <Route
          path="/data-recovery"
          element={
            <RoleRoute allowedRoles={["admin"]}>
              <DataRecovery />
            </RoleRoute>
          }
        />
      </Routes>
    </AuthContext.Provider>
  );
};

export default function App() {
  return (
    <Router>
      <DemoProvider>
        <AppLayout />
      </DemoProvider>
    </Router>
  );
}